import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Login from '../components/auth/Login';
import Register from '../components/auth/Register';

export const SESSION_ENDED_MESSAGE = 'Your session has ended. Please sign in again.';

/**
 * `from` is honoured only as a same-origin path ("/media/folder/3"), never a
 * protocol-relative or absolute URL, and never the sign-in page itself.
 */
export function safeReturnPath(from: string | null): string {
  if (!from || !from.startsWith('/') || from.startsWith('//') || from.startsWith('/\\')) return '/';
  if (from === '/auth' || from.startsWith('/auth?') || from.startsWith('/auth/')) return '/';
  return from;
}

const Auth: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === '1';
  const target = safeReturnPath(searchParams.get('from'));

  // Signed in (on arrival, or by the form just now): go on to `from`, else Home.
  useEffect(() => {
    if (!isLoading && user) {
      navigate(target, { replace: true });
    }
  }, [user, isLoading, navigate, target]);

  // Only while a stored session is being checked on start; never during a submit.
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isLogin ? (
        <Login
          notice={expired ? SESSION_ENDED_MESSAGE : undefined}
          onSwitchToRegister={() => setIsLogin(false)}
        />
      ) : (
        <Register onSwitchToLogin={() => setIsLogin(true)} />
      )}
    </div>
  );
};

export default Auth;
